/**
 * Error strings reported by the `storage:{n}` component in device status.
 */
export const STORAGE_ERROR = {
	NEEDS_FORMATTING: 'needs_formatting',
	MOUNTING: 'mounting',
	MOUNT_FAILED: 'mount_failed',
	MOUNTING_NFS: 'mounting_nfs',
	MOUNTING_CIFS: 'mounting_cifs',
	UNMOUNTING_NFS: 'unmounting_nfs',
	UNMOUNTING_CIFS: 'unmounting_cifs',
	UNMOUNT_FAILED: 'unmount_failed',
	RESOLVING_NFS_HOST: 'resolving_nfs_host',
	RESOLVING_CIFS_HOST: 'resolving_cifs_host',
	FILESYSTEM_ERROR: 'filesystem_error',
	WAITING_FOR_NETWORK: 'waiting_for_network',
	FORMATTING: 'formatting',
	FORMAT_FAILED: 'format_failed',
	EJECT_FAILED: 'eject_failed',
	EJECTED: 'ejected',
} as const;

export type StorageError = (typeof STORAGE_ERROR)[keyof typeof STORAGE_ERROR];

/**
 * LEGACY pre-release firmware error strings mapped to their canonical form.
 *
 * @ToDo remove when camera is released with final firmware
 */
export const LEGACY_STORAGE_ERROR_MAP = {
	'needs format': STORAGE_ERROR.NEEDS_FORMATTING,
	'mount failed': STORAGE_ERROR.MOUNT_FAILED,
	'mounting nfs': STORAGE_ERROR.MOUNTING_NFS,
	'mounting cifs': STORAGE_ERROR.MOUNTING_CIFS,
	'unmounting nfs': STORAGE_ERROR.UNMOUNTING_NFS,
	'unmounting cifs': STORAGE_ERROR.UNMOUNTING_CIFS,
	'unmount failed': STORAGE_ERROR.UNMOUNT_FAILED,
	'resolving nfs host': STORAGE_ERROR.RESOLVING_NFS_HOST,
	'resolving cifs host': STORAGE_ERROR.RESOLVING_CIFS_HOST,
	'filesystem error': STORAGE_ERROR.FILESYSTEM_ERROR,
	'format failed': STORAGE_ERROR.FORMAT_FAILED,
} as const satisfies Record<string, StorageError>;

const STORAGE_ERROR_VALUES = new Set<string>(Object.values(STORAGE_ERROR));

export const isStorageError = (error: string): error is StorageError => STORAGE_ERROR_VALUES.has(error);

/**
 * Resolve a raw storage error from device status to its canonical form.
 */
export const getStorageError = (error: string): StorageError | undefined => {
	if (isStorageError(error)) {
		return error;
	}

	if (error in LEGACY_STORAGE_ERROR_MAP) {
		return LEGACY_STORAGE_ERROR_MAP[error as keyof typeof LEGACY_STORAGE_ERROR_MAP];
	}

	return undefined;
};

/**
 * Error strings reported by the `camera:{n}` component in device status.
 */
export const CAMERA_ERROR = {
	/** Streamer filesystem is incompatible; a streamer firmware update is required. */
	STREAMER_FS_BAD: 'streamer_fs_bad',
} as const;

export type CameraError = (typeof CAMERA_ERROR)[keyof typeof CAMERA_ERROR];
